import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { PricingCard } from '../components/pages/pricing/PricingCard';
import { Heading } from '../components/typography/Heading';
import { Text } from '../components/typography/Text';
import { FadeIn } from '../components/animation/FadeIn';
import { Mail } from 'lucide-react';
import { loadPrices, toPlan } from '../components/pages/Pricing';
import type { Plan, Product } from '../components/pages/Pricing';

export default function GetOffersPricing() {
  const [plan, setPlan] = useState<Plan | null>(null);
  const [interviewSlot, setInterviewSlot] = useState<{start: string, end: string, time: string} | null>(null);

  useEffect(() => {
    loadPrices((data: Product[]) => {
      const validPlan = data
        .map((it: Product) => toPlan(it))
        .filter((plan): plan is Plan => plan !== null)
        .find((plan) => plan.stripeButtonId === 'prod_SOFFCXdUskOzjH');

      if (validPlan) {
        setPlan(validPlan);
      }
    });
    // Получаем диапазон дат и время из localStorage
    const start = localStorage.getItem('interviewStartDate');
    const end = localStorage.getItem('interviewEndDate');
    const time = localStorage.getItem('interviewTime');
    if (start && end && time) {
      setInterviewSlot({ start, end, time });
    }
  }, []);

  if (!plan) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-16 min-h-screen">
      <FadeIn className="flex flex-col items-center text-center mb-16">
        <Heading level={1} align="center" className="mb-4">
          Get Interview Offers
        </Heading>
        <Text size="lg" align="center" className="text-slate-light max-w-2xl">
          Choose your plan to start receiving interview offers tailored to your profile.
        </Text>
      </FadeIn>

      <div className="flex flex-col md:flex-row gap-10 items-start justify-center max-w-5xl mx-auto">
        <div className="flex-1 bg-white rounded-2xl shadow-xl p-8 mb-10 md:mb-0 max-w-lg w-full border border-slate-100 flex flex-col justify-between min-h-[420px] h-full">
          <div>
            <h3 className="text-2xl font-extrabold mb-6 text-gray-900 tracking-tight flex items-center gap-2">
              <span className="inline-block bg-primary/10 text-primary px-3 py-1 rounded-full text-base font-semibold mr-2">Your Offer Summary</span>
            </h3>
            <div className="divide-y divide-gray-100 bg-gray-50 rounded-xl p-6">
              <div className="flex items-center gap-3 pb-4">
                <span className="bg-blue-100 text-blue-600 rounded-full p-2"><svg width='20' height='20' fill='none'><path d='M10 2v16M2 10h16' stroke='#3b82f6' strokeWidth='2' strokeLinecap='round'/></svg></span>
                <div>
                  <div className="text-xs text-gray-500">Industry</div>
                  <div className="font-semibold text-gray-900 text-lg">Technology</div>
                </div>
              </div>
              <div className="flex items-center gap-3 py-4">
                <span className="bg-green-100 text-green-600 rounded-full p-2"><svg width='20' height='20' fill='none'><path d='M10 2a8 8 0 110 16 8 8 0 010-16z' stroke='#10b981' strokeWidth='2'/></svg></span>
                <div>
                  <div className="text-xs text-gray-500">Region</div>
                  <div className="font-semibold text-gray-900 text-lg">California</div>
                </div>
              </div>
              <div className="flex items-center gap-3 py-4">
                <span className="bg-yellow-100 text-yellow-600 rounded-full p-2"><svg width='20' height='20' fill='none'><path d='M4 10a6 6 0 1112 0A6 6 0 014 10zm6 2v2m0-8v2' stroke='#f59e42' strokeWidth='2' strokeLinecap='round'/></svg></span>
                <div>
                  <div className="text-xs text-gray-500">Expected Salary</div>
                  <div className="font-semibold text-gray-900 text-lg">$120,000 / year</div>
                </div>
              </div>
              <div className="flex items-center gap-3 py-4">
                <span className="bg-purple-100 text-purple-600 rounded-full p-2"><svg width='20' height='20' fill='none'><path d='M10 2v16m0 0l4-4m-4 4l-4-4' stroke='#a78bfa' strokeWidth='2' strokeLinecap='round'/></svg></span>
                <div>
                  <div className="text-xs text-gray-500">Experience</div>
                  <div className="font-semibold text-gray-900 text-lg">5 years</div>
                </div>
              </div>
              <div className="flex items-center gap-3 pt-4">
                <span className="bg-orange-100 text-orange-600 rounded-full p-2"><svg width='20' height='20' fill='none'><path d='M10 2v4m0 8v4m-4-4h8' stroke='#fb923c' strokeWidth='2' strokeLinecap='round'/></svg></span>
                <div>
                  <div className="text-xs text-gray-500">Interview Slot</div>
                  <div className="font-semibold text-gray-900 text-lg">
                    {interviewSlot
                      ? `${interviewSlot.start} – ${interviewSlot.end}, ${interviewSlot.time.charAt(0).toUpperCase() + interviewSlot.time.slice(1)}`
                      : 'Not selected'}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 p-4 bg-gradient-to-r from-primary/5 via-blue-500/5 to-primary/5 rounded-xl text-gray-700 text-base">
            <span className="font-semibold text-primary">What you get:</span> <br />
            Guaranteed interview invitations, priority matching with top employers, and a dedicated support team to help you land your next job faster.
          </div>
        </div>
        <div className={`flex-1 flex flex-col items-center w-full max-w-md ${plan.features.length > 3 ? 'h-full' : ''}`}> 
          <div className={`w-full ${plan.features.length > 3 ? 'h-full flex items-stretch justify-center' : ''}`}> 
            <div className={`w-full flex flex-col justify-center ${plan.features.length > 3 ? 'h-full min-h-[420px]' : 'min-h-0'}`}> 
              <PricingCard {...plan} />
            </div>
          </div>
        </div>
      </div>

      <div className="text-center mt-16 space-y-8">
        <Text className="text-slate-light">
          14-day money-back guarantee. No questions asked.
        </Text>

        <div className="max-w-2xl mx-auto bg-gradient-to-r from-primary/5 via-blue-500/5 to-primary/5 rounded-2xl p-8">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Mail className="w-5 h-5 text-primary" />
            <Text className="text-slate font-medium">Need help choosing?</Text>
          </div>
          <Text className="text-slate-light">
            Have questions or want to share feedback? Email us at{' '}
            <a 
              href="mailto:info@coverlettergenerator.ai" 
              className="text-primary hover:text-primary-hover transition-colors"
            >
              info@coverlettergenerator.ai
            </a>
            {' '}— we respond to everyone!
          </Text>
        </div>
      </div>
    </div>
  );
} 